import React, { useEffect, useRef, useState } from 'react';
import {
  useApi,
  identityApiRef,
  configApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import type { FieldValidation } from '@rjsf/utils';

type VerificationState =
  | { status: 'idle' }
  | { status: 'invalid-email' }
  | { status: 'loading' }
  | { status: 'found'; handle: string }
  | { status: 'not-found'; handle: string }
  | { status: 'error'; message: string };

const DEBOUNCE_MS = 600;
const NESTLE_EMAIL_REGEX = /^[\w\-.]+@([\w-]+\.)?nestle\.com$/i;

export const GithubUserVerification = ({
  onChange,
  uiSchema,
  formContext,
  schema: { title, description },
}: FieldExtensionComponentProps<string>) => {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);

  const uiOptions = (uiSchema?.['ui:options'] as Record<string, unknown>) ?? {};
  const emailField = uiOptions.emailField as string | undefined;
  const preventNonNestleUsers = uiOptions.preventNonNestleUsers !== false;

  const formEmail: string | undefined = emailField
    ? ((formContext as any)?.formData?.[emailField] ?? undefined)
    : undefined;

  const [state, setState] = useState<VerificationState>({ status: 'idle' });
  const [identityEmail, setIdentityEmail] = useState<string | undefined>(undefined);
  const [identityLoaded, setIdentityLoaded] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastVerifiedEmailRef = useRef<string | undefined>(undefined);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const abortControllerRef = useRef<AbortController>();
  const identityFetchedRef = useRef(false);

  // Resolve the logged-in user's email once (only when emailField is not set, or as fallback)
  useEffect(() => {
    if (identityFetchedRef.current) return;
    identityFetchedRef.current = true;

    const fetchIdentity = async () => {
      try {
        const { email } = await identityApi.getProfileInfo();
        if (email) {
          setIdentityEmail(email);
        }
      } catch {
        // Identity not available — that's fine, user can enter email manually
      }
      setIdentityLoaded(true);
    };

    fetchIdentity();
  }, [identityApi]);

  // Determine which email to verify:
  // - If emailField is set and has a value → use that
  // - If emailField is set but empty → fall back to identity email
  // - If emailField is not set → use identity email directly
  const emailToVerify = emailField
    ? (formEmail && formEmail.trim() ? formEmail.trim() : identityEmail)
    : identityEmail;

  useEffect(() => {
    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    }

    // If no email available at all, stay idle
    if (!emailToVerify) {
      if (lastVerifiedEmailRef.current !== undefined) {
        lastVerifiedEmailRef.current = undefined;
        setState({ status: 'idle' });
        onChangeRef.current(undefined);
      }
      return;
    }

    // If email is present but not a valid Nestlé domain, show warning
    if (preventNonNestleUsers && !NESTLE_EMAIL_REGEX.test(emailToVerify)) {
      if (lastVerifiedEmailRef.current !== undefined) {
        lastVerifiedEmailRef.current = undefined;
      }
      setState({ status: 'invalid-email' });
      onChangeRef.current(undefined);
      return;
    }

    // If we already verified this exact email, skip
    if (emailToVerify === lastVerifiedEmailRef.current) {
      return;
    }

    // Show loading immediately and debounce the API call
    setState({ status: 'loading' });

    debounceTimerRef.current = setTimeout(() => {
      // Abort any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const verify = async () => {

        try {
          const backendUrl = configApi.getString('backend.baseUrl');
          const response = await fetchApi.fetch(
            `${backendUrl}/api/github-users/verify`,
            {
              method: 'POST',
              signal: controller.signal,
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: emailToVerify }),
            },
          );

          if (controller.signal.aborted) return;

          const data = await response.json();

          if (!response.ok) {
            setState({
              status: 'error',
              message: data?.error ?? `Backend returned ${response.status}`,
            });
            onChangeRef.current(undefined);
            return;
          }

          lastVerifiedEmailRef.current = emailToVerify;

          if (data.exists) {
            setState({ status: 'found', handle: data.login });
            onChangeRef.current(data.login);
          } else {
            setState({ status: 'not-found', handle: data.login });
            onChangeRef.current(undefined);
          }
        } catch (error) {
          if (controller.signal.aborted) return;
          setState({
            status: 'error',
            message: (error as Error)?.message ?? 'Unexpected error during verification',
          });
          onChangeRef.current(undefined);
        }
      };

      verify();
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [emailToVerify, preventNonNestleUsers, configApi, fetchApi]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  let idleAlertSeverity: 'error' | 'info' | 'warning' = 'info';
  if (!identityEmail) {
    idleAlertSeverity = emailField ? 'warning' : 'error';
  }

  let idleMessage = 'Unable to determine your email from your login session.';
  if (emailField && identityEmail) {
    idleMessage = preventNonNestleUsers
      ? 'Enter a valid Nestlé email above to verify the GitHub account, or leave empty to use your own.'
      : 'Enter an email above to verify the GitHub account, or leave empty to use your own.';
  }
  if (emailField && !identityEmail) {
    idleMessage = preventNonNestleUsers
      ? 'Could not determine your email from your login. Please enter a Nestlé email above to verify the GitHub account.'
      : 'Could not determine your email from your login. Please enter an email above to verify the GitHub account.';
  }

  return (
    <FormControl margin="normal" fullWidth>
      {title && (
        <Typography variant="subtitle2" gutterBottom>
          {title}
        </Typography>
      )}
      {description && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {description}
        </Typography>
      )}
      <Box mt={1}>
        {state.status === 'idle' && !identityLoaded && (
          <Box display="flex" alignItems="center" gridGap={8}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              Loading user information...
            </Typography>
          </Box>
        )}
        {state.status === 'idle' && identityLoaded && (
          <Alert severity={idleAlertSeverity}>
            {idleMessage}
          </Alert>
        )}
        {state.status === 'invalid-email' && (
          <Alert severity="warning">
            {emailField
              ? 'Please enter a valid Nestlé email address (e.g. name@nestle.com).'
              : 'Your login email is not a recognized Nestlé address.'}
          </Alert>
        )}
        {state.status === 'loading' && (
          <Box display="flex" alignItems="center" gridGap={8}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              Verifying GitHub account...
            </Typography>
          </Box>
        )}
        {state.status === 'found' && (
          <Alert severity="success">
            GitHub account detected: <strong>{state.handle}</strong>
          </Alert>
        )}
        {state.status === 'not-found' && (
          <Alert severity="error">
            No GitHub Enterprise account found{emailField ? ' for this email' : ''} (expected GitHub Login:{' '}
            <strong>{state.handle}</strong>).
            {emailField
              ? ' Please ensure the user has an active GitHub Enterprise account before proceeding.'
              : ' Your login does not have an associated GitHub Enterprise account.'}
          </Alert>
        )}
        {state.status === 'error' && (
          <Alert severity="error">
            Unable to verify GitHub account: {state.message}
          </Alert>
        )}
      </Box>
    </FormControl>
  );
};

export const GithubUserVerificationValidation = (
  value: string,
  validation: FieldValidation,
) => {
  if (!value) {
    validation.addError(
      'A valid GitHub Enterprise account is required to proceed.',
    );
  }
};
